// src/pages/Reports/MarketingReports.js
// Reports & Analytics for MarketingManager (team) / MarketingExecutive (own).
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Download } from 'lucide-react';
import { selectAuthState } from '../../store/selectors/authSelector';
import {
  fetchMyReports,
  fetchAllReports,
  fetchExecutiveSummary,
} from '../../services/features/visitReport/visitReportSlice';
import {
  computeRange,
  formatDateKey,
  formatDateTime,
  shiftDateKey,
  FLOW_META,
} from '../../utils/attendanceFormat';
import '../Shared/ReportPages.css';

const PRESETS = [
  { key: 'week', label: '7 Days' },
  { key: 'month', label: 'This Month' },
  { key: 'all', label: 'All Time' },
];

const csvCell = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;

const MarketingReports = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { role } = useSelector(selectAuthState);
  const isManager = role === 'MarketingManager';
  const { myList, allList, loading, execSummary } = useSelector((s) => s.visitReport);

  const [range, setRange] = useState('month');
  const { from, to } = useMemo(() => computeRange(range), [range]);

  useEffect(() => {
    const p = { from: from || undefined, to: to || undefined };
    dispatch(isManager ? fetchAllReports({ ...p, limit: 500 }) : fetchMyReports({ ...p, limit: 500 }));
    dispatch(fetchExecutiveSummary(p));
  }, [dispatch, from, to, isManager]);

  const list = isManager ? allList : myList;

  const stats = useMemo(() => {
    const flow = { HOT: 0, WARM: 0, COLD: 0 };
    const byDay = {};
    const byArea = {};
    const customers = new Set();
    let revisits = 0;
    list.forEach((r) => {
      if (flow[r.statusFlow] !== undefined) flow[r.statusFlow]++;
      const day = r.visitDateKey || formatDateKey(r.submittedAt);
      byDay[day] = (byDay[day] || 0) + 1;
      const area = `${r.taluk || '—'}, ${r.district || '—'}`;
      byArea[area] = byArea[area] || { area, total: 0, hot: 0 };
      byArea[area].total++;
      if (r.statusFlow === 'HOT') byArea[area].hot++;
      customers.add(r.mobile);
      if (r.isRevisit) revisits++;
    });

    // Daily series: every day in range (max 31), or last days that have data
    let days = [];
    if (from && to) {
      let d = to;
      for (let i = 0; i < 31 && d >= from; i++) {
        days.unshift(d);
        d = shiftDateKey(d, -1);
      }
    } else {
      days = Object.keys(byDay).sort().slice(-31);
    }
    const series = days.map((d) => ({ day: d, value: byDay[d] || 0 }));

    return {
      flow,
      series,
      areas: Object.values(byArea).sort((a, b) => b.total - a.total).slice(0, 10),
      customers: customers.size,
      revisits,
      hotRate: list.length ? Math.round((flow.HOT / list.length) * 100) : 0,
    };
  }, [list, from, to]);

  const maxDay = Math.max(1, ...stats.series.map((s) => s.value));

  const downloadCsv = () => {
    const head = ['Date', 'Customer', 'Shop', 'Mobile', 'Taluk', 'District', 'Business', 'Lead', 'Visit No', 'Submitted by', 'Report'];
    const rows = list.map((r) => [
      formatDateTime(r.submittedAt), r.name, r.businessName, r.mobile, r.taluk, r.district, r.business,
      r.statusFlow, r.visitNumber || 1, r.executiveName || r.executiveId?.name || '', r.report,
    ]);
    const csv = [head, ...rows].map((row) => row.map(csvCell).join(',')).join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `visit-reports-${formatDateKey()}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const total = list.length;
  const kpis = [
    { label: 'Total Visits', value: total, color: 'var(--text-primary)' },
    { label: 'Hot', value: stats.flow.HOT, color: FLOW_META.HOT.color },
    { label: 'Warm', value: stats.flow.WARM, color: FLOW_META.WARM.color },
    { label: 'Cold', value: stats.flow.COLD, color: FLOW_META.COLD.color },
    { label: 'Hot Rate', value: `${stats.hotRate}%`, color: FLOW_META.HOT.color },
    { label: 'Customers', value: stats.customers, color: '#14b8a6' },
    { label: 'Revisits', value: stats.revisits, color: '#8b5cf6' },
  ];

  return (
    <div className="rp-page">
      <div className="rp-toolbar">
        <div className="rp-chips">
          {PRESETS.map((p) => (
            <button key={p.key} className={`rp-chip ${range === p.key ? 'active' : ''}`} onClick={() => setRange(p.key)}>
              {p.label}
            </button>
          ))}
        </div>
        <div className="rp-spacer" />
        <button className="rp-chip" onClick={() => navigate(isManager ? '/visit-reports' : '/visit-reports/list')}>
          Open Visit Reports
        </button>
        <button className="rp-chip active" onClick={downloadCsv} disabled={total === 0}>
          <Download size={14} style={{ verticalAlign: -2, marginRight: 6 }} /> Download CSV
        </button>
      </div>

      <div className="rp-kpis">
        {kpis.map((k) => (
          <div key={k.label} className="rp-kpi" style={{ '--kpi': k.color }}>
            <div className="rp-kpi-value">{k.value}</div>
            <div className="rp-kpi-label">{k.label}</div>
          </div>
        ))}
      </div>

      {loading && total === 0 ? (
        <div className="rp-card"><div className="rp-loading">Loading…</div></div>
      ) : total === 0 ? (
        <div className="rp-card">
          <div className="rp-empty">
            <b>No visit reports in this period</b>
            Try “All Time”, or submit a new visit report.
          </div>
        </div>
      ) : (
        <>
          <div className="rp-split">
            <div className="rp-card rp-card-pad">
              <h3 className="rp-card-title">Visits per day</h3>
              <div className="rp-trend">
                {stats.series.map((s) => (
                  <div key={s.day} className="rp-trend-col" title={`${s.day}: ${s.value}`}>
                    <div className="rp-trend-val">{s.value || ''}</div>
                    <div className="rp-trend-bar" style={{ height: `${(s.value / maxDay) * 100}%` }} />
                    <div className="rp-trend-lbl">{s.day.slice(8)}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="rp-card rp-card-pad">
              <h3 className="rp-card-title">Lead status</h3>
              {['HOT', 'WARM', 'COLD'].map((f) => {
                const m = FLOW_META[f];
                const pct = total ? Math.round((stats.flow[f] / total) * 100) : 0;
                return (
                  <div key={f} style={{ marginBottom: 14 }}>
                    <div className="rp-sumline" style={{ padding: 0, marginBottom: 6 }}>
                      <span>{m.label} · {m.hint}</span>
                      <b>{stats.flow[f]} ({pct}%)</b>
                    </div>
                    <div className="rp-bar" style={{ height: 10 }}>
                      <div style={{ width: `${pct}%`, background: m.color }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="rp-split">
            <div className="rp-card">
              <h3 className="rp-card-title" style={{ padding: '16px 18px 0' }}>Top areas (taluk, district)</h3>
              <div className="rp-table-wrap">
                <table className="rp-table">
                  <thead><tr><th>Area</th><th className="rp-num">Visits</th><th className="rp-num">Hot</th></tr></thead>
                  <tbody>
                    {stats.areas.map((a) => (
                      <tr key={a.area}>
                        <td>{a.area}</td>
                        <td className="rp-num rp-strong">{a.total}</td>
                        <td className="rp-num">{a.hot}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {isManager && (
              <div className="rp-card">
                <h3 className="rp-card-title" style={{ padding: '16px 18px 0' }}>By team member</h3>
                <div className="rp-table-wrap">
                  <table className="rp-table">
                    <thead><tr><th>Person</th><th className="rp-num">Visits</th><th className="rp-num">Hot</th></tr></thead>
                    <tbody>
                      {(execSummary?.people || []).map((p) => (
                        <tr key={String(p.executiveId)}>
                          <td>{p.name}<div className="rp-sub">{p.role}</div></td>
                          <td className="rp-num rp-strong">{p.total}</td>
                          <td className="rp-num">{p.hot}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default MarketingReports;
